import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Chip, CircularProgress, Stack, Divider, Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, AutoAwesome } from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { arabicColumnLabel } from '../lib/columnLabels';

interface AuditRow {
  id: number;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  actor_email: string | null;
  source: string | null;
  changed_fields: string[] | null;
  created_at: string;
}

const OP: Record<AuditRow['operation'], { color: 'success' | 'info' | 'error'; icon: React.ReactElement }> = {
  INSERT: { color: 'success', icon: <Add sx={{ fontSize: 15 }} /> },
  UPDATE: { color: 'info', icon: <Edit sx={{ fontSize: 15 }} /> },
  DELETE: { color: 'error', icon: <Delete sx={{ fontSize: 15 }} /> },
};

interface Props {
  table: string;
  recordId: string | number;
}

// Compact change history for a single record, read from audit_logs.
const RecordHistory: React.FC<Props> = ({ table, recordId }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar-EG' : 'en-US';
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('audit_logs')
        .select('id,operation,actor_email,source,changed_fields,created_at')
        .eq('table_name', table)
        .eq('record_id', String(recordId))
        .order('created_at', { ascending: false })
        .limit(30);
      setRows((data || []) as AuditRow[]);
    } finally {
      setLoading(false);
    }
  }, [table, recordId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const verb = (op: AuditRow['operation']) =>
    op === 'INSERT' ? t('activity.created') : op === 'UPDATE' ? t('activity.updated') : t('activity.deleted');

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>;
  }
  if (rows.length === 0) {
    return <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{t('activity.noActivity')}</Typography>;
  }

  return (
    <Stack divider={<Divider flexItem />} spacing={1}>
      {rows.map((r) => {
        const meta = OP[r.operation];
        return (
          <Box key={r.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Chip size="small" color={meta.color} icon={meta.icon} label={verb(r.operation)}
              sx={{ height: 22, fontSize: '0.7rem' }} />
            {r.source === 'agent' && (
              <Tooltip title={t('activity.byAgentTip')}>
                <Chip size="small" color="secondary" variant="outlined" icon={<AutoAwesome sx={{ fontSize: 13 }} />}
                  label={t('activity.byAgent')} sx={{ height: 22, fontSize: '0.68rem' }} />
              </Tooltip>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {(r.actor_email || t('activity.system'))} · {new Date(r.created_at).toLocaleString(locale)}
              </Typography>
              {r.operation === 'UPDATE' && r.changed_fields?.length ? (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  {r.changed_fields.map(arabicColumnLabel).join('، ')}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default RecordHistory;
